import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";

const CROPPED_SIZE = 300;

/**
 * Choosing a profile picture: the file is read, cropped to a square in a bottom sheet, and the
 * result posted as base64.
 */
export default class extends Controller<HTMLElement> {
  static override targets = [
    "blob",
    "crop",
    "file",
    "input",
    "name",
    "preview",
    "sheet",
  ];

  /** The "Photo chosen {0}" sentence, so the browser says it the way the server would have. */
  static override values = { chosen: String };

  declare readonly blobTarget: HTMLInputElement;
  declare readonly chosenValue: string;
  declare readonly cropTarget: HTMLImageElement;
  declare readonly fileTarget: HTMLElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly inputTargets: HTMLInputElement[];
  declare readonly nameTarget: HTMLElement;
  declare readonly previewTarget: HTMLImageElement;
  declare readonly sheetTarget: HTMLDialogElement;

  private cropper: Cropper | undefined;
  private initialSource = "";

  /*
   * No `this.x = this.x.bind(this)` here any more. Every one of these is reached through a
   * `data-action` attribute, and Stimulus invokes the method on the controller, so the bindings
   * were rebuilding five functions on every connect to no effect.
   */
  override connect(): void {
    this.initialSource = this.cropTarget.src;
  }

  override disconnect(): void {
    this.cropper?.destroy();
    this.cropper = undefined;
  }

  /* Every way out of the sheet ends here, because `close` is what Cancel, Escape, a click on the
     backdrop and a pull downwards all raise. Tearing the cropper down in Cancel's own handler
     instead would leave it alive behind the other three. */
  onSheetClose(): void {
    this.resetCropper();

    this.fileTarget.hidden = true;
    this.nameTarget.textContent = "";
  }

  initCropper(image: string): void {
    this.cropTarget.src = image;
    this.cropper = new Cropper(this.cropTarget, { aspectRatio: 1 });
  }

  resetCropper(): void {
    this.cropTarget.src = this.initialSource;
    this.cropper?.destroy();
    this.cropper = undefined;
  }

  openSheet(): void {
    this.sheetTarget.showModal();
  }

  onChange(): void {
    /* `find` then `?.` rather than `find(...).files[0]`: with no file chosen `find` answers
           undefined, and the old chain read a property straight off it and threw. */
    const file = this.inputTargets
      .map((it) => it.files?.[0])
      .find((it) => it !== undefined);
    if (file === undefined) {
      return;
    }

    this.nameTarget.textContent = this.chosenValue
      ? this.chosenValue.replace("{0}", file.name)
      : file.name;
    this.fileTarget.hidden = false;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        /* The sheet opens first. Nothing inside a closed <dialog> has a layout, and a cropper
           built in there measures a box of no size: it came up with the photograph at its full
           width and no crop area on it. */
        this.openSheet();
        this.initCropper(reader.result);
      }
    };

    reader.readAsDataURL(file);
  }

  save(event: Event): void {
    if (this.cropper === undefined) {
      return;
    }

    const blob = this.cropper
      .getCroppedCanvas({ height: CROPPED_SIZE, width: CROPPED_SIZE })
      .toDataURL();

    this.blobTarget.value = blob.split(",")[1] ?? "";
    this.inputTarget.value = "";
    this.previewTarget.src = blob;

    /* Closing raises `close`, which is what resets the cropper -- see `onSheetClose`. */
    this.sheetTarget.close();

    event.preventDefault();

    /* Cropping is the confirmation, so cropping is what saves. The app uploads the moment the
           picker returns; the screen this is on has no Save button of its own any more, and a
           cropped picture sitting in a hidden field waiting for one is a picture you think you
           changed. `requestSubmit` and not `submit`, so the form's own validation still runs. */
    this.element.closest("form")?.requestSubmit();
  }
}
